/* eslint-disable import/no-cycle */
/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import React, { FunctionComponent, ReactText, useEffect, useMemo, useState } from "react";
import isEqual from "lodash/isEqual";
import { useTranslation } from "react-i18next";
import { Button } from "components/Novus/Button";
import { CheckBox } from "components/Novus/CheckBox";
import { Input } from "components/Novus/Input";
import { BoxTableModal, Scrollable } from "./styles";
import { GenericType } from "../../../curb/map/deckgl/interfaces";

export const CustomFilter: FunctionComponent<{
    props: GenericType;
    handleClear: () => void;
    handleApply: (checkedValues: string[]) => void;
    selectedValue: string[];
    selectOptions: Array<{ label: string; value: ReactText }>;
    isSorted: boolean;
}> = ({ props, handleClear, handleApply, selectedValue, selectOptions, isSorted }) => {
    const { column } = props;
    const { t } = useTranslation();
    const toggleTooltip = (
        event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
    ): void => {
        event.stopPropagation();
        (document.getElementById(column.id) as HTMLElement).style.display = "none";
        (document.getElementById(column.id) as HTMLElement).classList.remove("showArrow");
    };

    const [checkedValues, setCheckedValues] = useState<string[]>(selectedValue);

    const [searchText, setSearchText] = useState<string>("");
    const [isAsc, setIsAsc] = useState(false);
    const [isDsc, setIsDsc] = useState(false);

    const onDescClick = (): void => {
        if (typeof column.isSortedDesc === "undefined" || !column.isSortedDesc) {
            column.toggleSortBy(true, false);
            setIsDsc(true);
            setIsAsc(false);
        }
    };

    const onAscClick = (): void => {
        if (column.isSortedDesc || typeof column.isSortedDesc === "undefined") {
            column.toggleSortBy(false, false);
            setIsAsc(true);
            setIsDsc(false);
        }
    };

    const clearRecentCheckedValues = (): void => {
        if (selectOptions) {
            selectOptions.forEach((ele) => {
                const checkboxEl = document.getElementById(
                    ele.label + ele.value + column.id
                ) as HTMLInputElement;

                if (selectedValue && selectedValue.includes(ele.value as string) && checkboxEl) {
                    checkboxEl.checked = true;
                } else {
                    checkboxEl.checked = false;
                }
            });
            setCheckedValues(selectedValue);
        }
    };

    const filteredSelectOptions = useMemo(() => {
        if (searchText) {
            return selectOptions.filter((ele) =>
                ele.label.toString().toLowerCase().includes(searchText.toLowerCase())
            );
        }

        return selectOptions;
    }, [selectOptions, searchText]);

    useEffect(() => {
        if (filteredSelectOptions) {
            filteredSelectOptions.forEach((ele) => {
                const checkboxEl = document.getElementById(
                    ele.label + ele.value + column.id
                ) as HTMLInputElement;

                if (checkboxEl) {
                    if (
                        selectedValue?.includes(ele.value as string) ||
                        checkedValues?.includes(ele.value as string)
                    ) {
                        checkboxEl.checked = true;
                    } else {
                        checkboxEl.checked = false;
                    }
                }
            });
            if (selectedValue.length > 0) setCheckedValues(selectedValue);
        }
        if (column.isSortedDesc || typeof column.isSortedDesc === "undefined") {
            setIsAsc(false);
            setIsDsc(true);
        } else {
            setIsAsc(true);
            setIsDsc(false);
        }
    }, [selectedValue, column, filteredSelectOptions]);

    return (
        <BoxTableModal className="filterDiv" id={column.id}>
            <div className="section-header">
                <h1>{t("Filters")}</h1>
                <div
                    aria-label="clear-all-btn"
                    className={`clear-all-btn ${selectedValue.length === 0 ? "disabled" : ""}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                        setCheckedValues([]);
                        handleClear();
                    }}
                    onKeyDown={() => {
                        handleClear();
                        setCheckedValues([]);
                    }}
                >
                    {t("Clear all")}
                </div>
            </div>

            <div className="section-body">
                <div className="scrollable-content">
                    <div className="search-filter-container">
                        <Input
                            className="w-100"
                            isSearchIcon={true}
                            placeholder={`${t("Search")} ${t("Filters")}`}
                            type="text"
                            value={searchText}
                            onInputChange={(text) => {
                                setSearchText(text);
                            }}
                        />
                    </div>
                    <Scrollable>
                        {filteredSelectOptions?.map((ele, index) => (
                            <CheckBox
                                key={ele.label}
                                checked={checkedValues.includes(ele.value as string)}
                                dataTestId="listview-filter-checkbox"
                                id={ele.label + ele.value + column.id}
                                text={t(ele.label)}
                                onChange={(isChecked) => {
                                    if (isChecked) {
                                        setCheckedValues([...checkedValues, ele.value as string]);
                                    } else {
                                        setCheckedValues(
                                            checkedValues.filter((i) => i !== ele.value)
                                        );
                                    }
                                }}
                            />
                        ))}
                    </Scrollable>
                </div>
                <div className="section-footer">
                    <Button
                        data-testid="apply-filter"
                        disabled={isEqual(checkedValues, selectedValue)}
                        variant="primary"
                        onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
                            handleApply(checkedValues);
                            toggleTooltip(event as React.MouseEvent<HTMLButtonElement>);
                        }}
                    >
                        {t("Apply")}
                    </Button>
                    <Button
                        data-testid="cancel-filter"
                        variant="secondary"
                        onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
                            toggleTooltip(event as React.MouseEvent<HTMLButtonElement>);
                            clearRecentCheckedValues();
                        }}
                    >
                        {t("Cancel")}
                    </Button>
                </div>
            </div>
        </BoxTableModal>
    );
};
